/**
 * CaisseTopNav — Barre de navigation horizontale flat pour mobile.
 *
 * Design "Flat Pro" (aligné sur CaisseVerticalHub Desktop) :
 *   - Cartes verticales compactes : bg-slate-800/60 uni
 *   - Border-top 3px colorée selon le module (accent visuel discret)
 *   - Icône dans une petite pastille colorée à faible opacité
 *   - Label blanc, aucun dégradé, aucun grain, aucun halo
 *
 * Scrollable horizontalement, placée juste sous le header sticky.
 * Bouton "≡ Plus" en dernier ouvre la page Hub complète.
 * Affichée UNIQUEMENT sur mobile (< md).
 */
import React from "react";
import {
  LayoutGrid, Calculator, Printer, FileText, ArrowLeftRight, BarChart3,
  Sunrise, Sparkles, Truck, ClipboardCheck, Menu as MenuIcon, Bell,
} from "lucide-react";

// Modules essentiels — chaque module a une couleur hex d'accent (border-top + icône).
const MODULES = [
  { key: "tables", label: "Tables", icon: LayoutGrid, hex: "#0d9488", target: "tables", roles: ["manager", "admin"] },
  { key: "commande", label: "Commande", icon: Calculator, hex: "#f59e0b", target: "commande", roles: ["server", "manager", "admin"] },
  { key: "bons", label: "Bons", icon: Printer, hex: "#f97316", target: "bons", badge: "bons" },
  { key: "invoices", label: "Factures", icon: FileText, hex: "#3b82f6", target: "invoices" },
  { key: "notifications", label: "Notifs", icon: Bell, hex: "#f59e0b", target: "notifications", roles: ["manager", "admin"] },
  { key: "reversement", label: "Reversement", icon: ArrowLeftRight, hex: "#06b6d4", target: "hebdo", subTarget: "reversement", roles: ["manager", "admin"] },
  { key: "stats", label: "Stats", icon: BarChart3, hex: "#22c55e", target: "stats", badge: "stats", roles: ["admin"] },
  { key: "journee", label: "Journée", icon: Sunrise, hex: "#f59e0b", target: "journee", roles: ["admin", "manager"] },
  { key: "field_stock", label: "Stock", icon: ClipboardCheck, hex: "#10b981", target: "field_stock", badge: "field_stock", roles: ["manager", "admin"] },
];

export default function CaisseTopNav({
  activeTab,
  setActiveTab,
  currentUser,
  badges = {},
  fieldStockPendingCount = 0,
  cuisineNeedsPending = { pending: 0, urgent: 0 },
  onOpenMenu,
  onReversement,
}) {
  const role = currentUser?.role || "server";
  const visibleModules = MODULES.filter((m) => !m.roles || m.roles.includes(role));

  const resolveBadge = () => 0;

  const isActive = (m) => {
    if (m.key === "reversement") return activeTab === "hebdo";
    return activeTab === m.target;
  };

  const handleClick = (m) => {
    if (m.key === "reversement" && typeof onReversement === "function") {
      onReversement();
      return;
    }
    setActiveTab(m.target);
  };

  return (
    <div
      className="ctn-root md:hidden sticky top-[52px] xs:top-[56px] z-40 bg-slate-950/95 border-b border-slate-800/80 backdrop-blur-md"
      data-testid="caisse-top-nav"
    >
      <style>{`
        .ctn-root, .ctn-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }
        .ctn-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .ctn-scroll::-webkit-scrollbar { display: none; }

        /* Carte flat pro — même langage visuel que CaisseVerticalHub */
        .ctn-card {
          position: relative;
          width: 60px;
          height: 62px;
          flex-shrink: 0;
          border-radius: 10px;
          padding: 5px 3px 4px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          gap: 3px;
          background: rgba(30, 41, 59, 0.6); /* slate-800/60 uni */
          border: 1px solid rgba(51, 65, 85, 0.4);
          border-top-width: 2px;
          border-top-color: transparent;
          transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
        }
        .ctn-card:hover {
          background: rgba(51, 65, 85, 0.7);
          border-color: rgba(71, 85, 105, 0.6);
        }
        .ctn-card:active { transform: scale(0.96); }
        .ctn-card[data-active="true"] {
          background: rgba(51, 65, 85, 0.85);
          border-color: rgba(100, 116, 139, 0.5);
        }

        .ctn-icon-box {
          width: 26px; height: 26px;
          border-radius: 7px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        .ctn-label {
          font-size: 9px;
          font-weight: 700;
          color: rgb(226, 232, 240);
          letter-spacing: 0.005em;
          line-height: 1.05;
          text-align: center;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ctn-card[data-active="true"] .ctn-label { color: white; }

        .ctn-badge {
          position: absolute;
          top: 2px; right: 2px;
          min-width: 15px; height: 15px;
          padding: 0 4px;
          border-radius: 5px;
          background: rgb(239, 68, 68);
          color: white;
          font-family: 'JetBrains Mono', monospace;
          font-size: 9px;
          font-weight: 700;
          line-height: 15px;
          text-align: center;
        }

        .ctn-menu {
          border-top-color: rgba(251, 191, 36, 0.5) !important;
        }
      `}</style>

      <div className="ctn-scroll flex items-center gap-1.5 px-2 py-1.5">
        {visibleModules.map((m) => {
          const Icon = m.icon;
          const active = isActive(m);
          const bVal = m.badge ? resolveBadge(m.badge) : 0;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => handleClick(m)}
              data-active={active}
              className="ctn-card"
              style={{ borderTopColor: m.hex }}
              data-testid={`caisse-topnav-${m.key}`}
            >
              {bVal > 0 && (
                <span className="ctn-badge">{bVal > 99 ? "99+" : bVal}</span>
              )}
              <div className="ctn-icon-box" style={{ background: `${m.hex}22` }}>
                <Icon className="w-3.5 h-3.5" strokeWidth={2.4} style={{ color: m.hex }} />
              </div>
              <span className="ctn-label">{m.label}</span>
            </button>
          );
        })}

        {/* Bouton "Plus" — ouvre la page Hub complète */}
        <button
          type="button"
          onClick={onOpenMenu}
          className="ctn-card ctn-menu"
          data-testid="caisse-topnav-menu"
        >
          <div className="ctn-icon-box" style={{ background: "rgba(251, 191, 36, 0.15)" }}>
            <MenuIcon className="w-3.5 h-3.5" strokeWidth={2.4} style={{ color: "#fbbf24" }} />
          </div>
          <span className="ctn-label" style={{ color: "#fcd34d" }}>Plus</span>
        </button>
      </div>
    </div>
  );
}
